import React from 'react';
import {
	Box,
	CircularProgress,
	Stack,
	Typography,
	alpha,
	useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import { surfaceCard } from '../../../Shared/surfaceCard';
import AssignmentChip from './AssignmentChip';

const formatTime = (iso) => {
	const d = dayjs(iso);
	return d.isValid() ? d.format('h:mm A') : '';
};

const TodayBoard = ({
	board,
	loading,
	isManager,
	onCreateForUser,
	onEditAssignment,
	onDueClick,
}) => {
	const theme = useTheme();
	const dateKey = board?.columns?.[0]?.date;
	const dueItems = (dateKey && board?.due_by_date?.[dateKey]) || [];
	const rows = board?.rows || [];

	if (loading && !board) {
		return (
			<Box
				sx={{
					...surfaceCard(theme),
					display: 'flex',
					justifyContent: 'center',
					py: 6,
				}}
			>
				<CircularProgress size={28} />
			</Box>
		);
	}

	return (
		<Stack spacing={2}>
			{dueItems.length > 0 && (
				<Box sx={{ ...surfaceCard(theme), p: 2 }}>
					<Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
						Due today
					</Typography>
					<Stack direction="row" flexWrap="wrap" gap={1}>
						{dueItems.map((item) => (
							<Box
								key={item.job_id}
								component="button"
								type="button"
								onClick={() => onDueClick?.(item)}
								sx={{
									border: '1px solid',
									borderColor: alpha(theme.palette.warning.main, 0.35),
									backgroundColor: alpha(theme.palette.warning.main, 0.12),
									borderRadius: 2,
									px: 1.5,
									py: 1,
									cursor: onDueClick ? 'pointer' : 'default',
									textAlign: 'left',
								}}
							>
								<Typography variant="body2" fontWeight={700}>
									Due - {item.job_name}
								</Typography>
								{item.customer_name ? (
									<Typography variant="caption" color="text.secondary">
										{item.customer_name}
									</Typography>
								) : null}
							</Box>
						))}
					</Stack>
				</Box>
			)}

			<Box sx={{ ...surfaceCard(theme), overflow: 'hidden' }}>
				{rows.length === 0 ? (
					<Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
						<Typography color="text.secondary">
							No one on the crew list yet.
						</Typography>
					</Box>
				) : (
					rows.map((row, index) => {
						const assignments =
							(dateKey && row.cells?.[dateKey]?.assignments) || [];
						return (
							<Box
								key={row.id}
								sx={{
									display: 'flex',
									gap: 2,
									px: { xs: 1.5, sm: 2 },
									py: 1.75,
									borderBottom:
										index < rows.length - 1 ? '1px solid' : 'none',
									borderColor: 'divider',
									alignItems: 'flex-start',
								}}
							>
								<Box sx={{ width: { xs: 96, sm: 140 }, flexShrink: 0, pt: 0.5 }}>
									<Typography variant="body2" fontWeight={700}>
										{row.label}
									</Typography>
									{isManager && (
										<Typography
											component="button"
											type="button"
											variant="caption"
											onClick={() =>
												onCreateForUser?.({
													row,
													date: dateKey,
												})
											}
											sx={{
												mt: 0.5,
												border: 'none',
												background: 'none',
												p: 0,
												color: 'primary.main',
												cursor: 'pointer',
												fontWeight: 600,
												textAlign: 'left',
											}}
										>
											+ Assign
										</Typography>
									)}
								</Box>
								<Box sx={{ flex: 1, minWidth: 0 }}>
									{assignments.length === 0 ? (
										<Typography variant="body2" color="text.secondary">
											Free
										</Typography>
									) : (
										assignments.map((assignment) => (
											<Box
												key={assignment.id}
												sx={{
													display: 'flex',
													alignItems: 'flex-start',
													gap: 1,
													mb: 0.75,
												}}
											>
												<Typography
													variant="caption"
													fontWeight={700}
													sx={{
														width: 88,
														flexShrink: 0,
														pt: 0.75,
														color: 'text.secondary',
													}}
												>
													{formatTime(assignment.starts_at)}
												</Typography>
												<Box sx={{ flex: 1, minWidth: 0 }}>
													<AssignmentChip
														assignment={assignment}
														onClick={isManager ? onEditAssignment : undefined}
													/>
												</Box>
											</Box>
										))
									)}
								</Box>
							</Box>
						);
					})
				)}
			</Box>
		</Stack>
	);
};

export default TodayBoard;
